import clsx from "clsx";
import { type ComponentProps, useCallback, useState } from "react";

import type { AccordionWithImagesType } from "@features/article-content/model/types";

import { Button } from "@shared/ui/button";
import { Icon } from "@shared/ui/icon";
import { Image } from "@shared/ui/image";
import { Body, Heading } from "@shared/ui/typography";
import { mod } from "@shared/utils";

import s from "./accordion.module.scss";

export type CompProps = ComponentProps<"div"> & {
  index: number;
  data: AccordionWithImagesType;
  activeAccordion: number | null;
  onAccordionClick: (id: number) => void;
  isOnceOpen: boolean;
};

export const Accordion = (props: CompProps) => {
  const { data, index, activeAccordion, onAccordionClick, isOnceOpen } = props;

  const [isOpen, setIsOpen] = useState(false);

  const toggleAccordion = () => {
    setIsOpen((prev) => !prev);
  };

  const mods = mod(s, {
    bgColor: data.backgroundColor,
  });

  const handleOnAccordionClick = useCallback(() => {
    if (isOnceOpen) {
      onAccordionClick(data.id);
    } else {
      toggleAccordion();
    }
  }, [data.id, isOnceOpen]);

  return (
    <div
      className={clsx(s.root, mods, {
        [s.open]: isOpen || data.id === activeAccordion,
      })}
    >
      <Button className={s.head} onClick={handleOnAccordionClick}>
        <div className={s.title}>
          <div className={s.count}>{index + 1}</div>
          {data?.title && <Heading level={6}>{data.title}</Heading>}
        </div>
        <Icon name="arrow-down" className={s.arrow} />
      </Button>
      <div className={s.content}>
        {data?.description && (
          <Body size="caption" className={s.description}>
            {data.description}
          </Body>
        )}
        {data?.image?.url && (
          <Image
            src={data.image.url}
            alt={data.image?.alternativeText ?? "accordion image"}
            height="100%"
            objectFit="cover"
            className={s.image}
          />
        )}
      </div>
    </div>
  );
};

Accordion.displayName = "Accordion";
